import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  CreditCard,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Building2,
  FolderOpen,
  ClipboardList,
  BarChart3,
  FileQuestion,
  Minus,
  Wallet,
  Plus,
  Sparkles,
  Search,
  Command,
  MessageCircle,
  FileWarning,
  Calculator,
  LineChart,
  Landmark,
  Receipt,
  UserSearch,
  TrendingUp,
  Database,
  Upload,
  PieChart,
  RefreshCw,
  BellRing
} from 'lucide-react';
import { cn } from '@/lib/utils';
import GlobalSearch from './GlobalSearch';
import NotificationCenter from './NotificationCenter';
import CommandPalette from './CommandPalette';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Clients', href: '/clients', icon: Building2 },
  { name: 'Workers', href: '/workers', icon: Users },
  { name: 'Services', href: '/services', icon: Briefcase },
  { name: 'Service Orders', href: '/service-orders', icon: ClipboardList },
  { name: 'Documents', href: '/documents', icon: FolderOpen },
  { name: 'Quotations', href: '/quotations', icon: FileQuestion },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Payments', href: '/payments', icon: CreditCard },
  { name: 'Wallets', href: '/wallet-reports', icon: Wallet },
  { name: 'Credit Notes', href: '/credit-notes', icon: Minus },
  { name: 'Expenses', href: '/expenses', icon: Receipt },
  { name: 'Govt Expenses', href: '/govt-expenses-report', icon: CreditCard },
  { name: 'Govt Services', href: '/govt-services', icon: Landmark },
  { name: 'Expiry Manager', href: '/expiry-management', icon: FileWarning },
  { name: 'Reminders', href: '/reminders', icon: Bell },
  { name: 'Notifications', href: '/notification-center', icon: MessageCircle },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Customer Reports', href: '/customer-reports', icon: UserSearch },
  { name: 'Profitability', href: '/service-profitability', icon: TrendingUp },
  { name: 'Report Builder', href: '/report-builder', icon: Database },
  { name: 'Visual Charts', href: '/visual-charts', icon: PieChart },
  { name: 'Bulk Import', href: '/bulk-import', icon: Upload },
  { name: 'Recurring Invoices', href: '/recurring-invoices', icon: RefreshCw },
  { name: 'Pay Reminders', href: '/payment-reminders', icon: BellRing },
  { name: 'Financials', href: '/financial-reports', icon: Calculator },
  { name: 'Analytics', href: '/analytics', icon: LineChart },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  // Global keyboard shortcut for Command Palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ⌘+K or Ctrl+K to open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="min-h-screen bg-subtle-pattern">
        {/* Modern Glass Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex flex-col glass-sidebar transition-all duration-300 ease-out',
            sidebarOpen ? 'w-64' : 'w-[72px]'
          )}
        >
          {/* Logo */}
          <div className="flex items-center h-16 px-4 border-b border-slate-200/60">
            <div className={cn('flex items-center gap-3', !sidebarOpen && 'justify-center w-full')}>
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] flex items-center justify-center text-white font-bold shadow-lg shadow-teal-500/20">
                <span className="text-lg">TC</span>
                <Sparkles className="absolute -top-1 -right-1 w-3.5 h-3.5 text-amber-400" />
              </div>
              {sidebarOpen && (
                <div className="flex flex-col">
                  <span className="font-semibold text-slate-800 tracking-tight">Typing CRM</span>
                  <span className="text-[10px] text-slate-500 font-medium">Business Suite</span>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navigation.map((item, index) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/' && location.pathname.startsWith(item.href));
                
                const navItem = (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'nav-item-active'
                        : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900',
                      !sidebarOpen && 'justify-center px-2'
                    )}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <item.icon className={cn(
                      'w-5 h-5 flex-shrink-0 transition-transform duration-200',
                      isActive && 'scale-110'
                    )} />
                    {sidebarOpen && <span>{item.name}</span>}
                  </Link>
                );

                if (!sidebarOpen) {
                  return (
                    <Tooltip key={item.name}>
                      <TooltipTrigger asChild>{navItem}</TooltipTrigger>
                      <TooltipContent side="right" className="font-medium">
                        {item.name}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return navItem;
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="border-t border-slate-200/60 p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    'w-full justify-start gap-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 h-auto py-2',
                    !sidebarOpen && 'justify-center px-2'
                  )}
                  data-testid="user-menu-button"
                >
                  <Avatar className="w-9 h-9 ring-2 ring-white shadow-md">
                    <AvatarFallback className="bg-gradient-to-br from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] text-white text-sm font-semibold">
                      {user?.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-semibold text-slate-800 truncate">{user?.full_name}</div>
                        <div className="text-xs text-slate-500">{user?.role}</div>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main content */}
        <div className={cn('transition-all duration-300 ease-out', sidebarOpen ? 'ml-64' : 'ml-[72px]')}>
          {/* Modern Header */}
          <header className="sticky top-0 z-40 h-16 border-b border-slate-200/60 bg-white/80 backdrop-blur-md">
            <div className="flex items-center h-full px-6 gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              
              {/* Search Button */}
              <Button
                variant="outline"
                onClick={() => setSearchOpen(true)}
                className="hidden md:flex items-center gap-2 text-slate-500 hover:text-slate-700 w-64 justify-start"
                data-testid="search-trigger"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Search...</span>
                <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-slate-100 px-1.5 font-mono text-[10px] font-medium text-slate-500">
                  <Command className="w-3 h-3" />K
                </kbd>
              </Button>
              
              <div className="flex-1" />
              
              {/* Notification Center */}
              <NotificationCenter />
              
              <Button
                onClick={() => navigate('/service-orders/new')}
                className="btn-primary gap-2"
                data-testid="quick-create-order"
              >
                <Plus className="w-4 h-4" />
                New Order
              </Button>
            </div>
          </header>

          {/* Page content */}
          <main className="p-6 max-w-[1400px] mx-auto">
            <div className="animate-fade-in">
              {children}
            </div>
          </main>
        </div>
        
        {/* Global Search */}
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        
        {/* Command Palette (⌘+K) */}
        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      </div>
    </TooltipProvider>
  );
}
